import defaultModuleRouterHandler from './defaultModuleRouterHandler';
import getCurrentRoute from './getCurrentRoute';
import isUnifiedSearchEnabled from 'mini-search-common/lib/utils/isUnifiedSearchEnabled';
import Module from 'mini-module-switcher/lib/schema/Module';
import optionsModuleRouterHandler from './optionsModuleRouterHandler';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { composeCleanupRouteHandler, composeRouterHandler } from './composeRouterHandler';
import { endMailSearchSession } from 'mini-3s-mail-search-store';
import { mailFolderCleanupRouteHandler, mailFolderRouteHandler } from './mailFolderRouteHandler';
import { mailViewHandler } from './mailViewHandler';
import { registerPeopleCardRoutes } from 'mini-people-card-route-store';
import { registerRoute as registerRouteFunction, RouterOptions } from 'owa-router';
import { registerRsvpViewRoutes } from './registerRsvpViewRoutes';
import { registerSearchRoutes } from 'mini-search-landing-view/lib/utils/registerSearchRoutes';
import { searchCleanupRouterHandler, searchRouterHandler } from './searchRouterHandler';
import {
    mailRouteHandler,
    mailRouteCleanup,
} from 'mini-mail-route-store/lib/route-handlers/mailRouteHandler';
import {
    mailReadingPaneCleanUpRouteHandler,
    mailReadingPaneRouteHandler,
} from './mailReadingPaneRouteHandler';
import {
    feedbackModuleRouterHandler,
    feedbackModuleCleanupRouterHandler,
} from './feedbackModuleRouterHandler';
import {
    mailItemViewReadingPaneRouteEntryHandler,
    mailItemViewReadingPaneRouteExitHandler,
} from './mailItemReadingPaneRouteHandler';

export let mailRouterOptions: RouterOptions = {
    routeGenerator: Promise.resolve(getCurrentRoute()),
    defaultRoute: '/',
};

export const initializeMailRoutes = (defaultRoute: string = '/') =>
    async function (registerRoute: typeof registerRouteFunction) {
        if (isUnifiedSearchEnabled()) {
            registerSearchRoutes(
                () => {
                    switchModule(Module.Search);
                } /* RouteHandler */,
                () => {
                    endMailSearchSession();
                }
            );
        } else {
            registerRoute(defaultRoute + 'search', searchRouterHandler, searchCleanupRouterHandler);
        }

        registerRoute(
            defaultRoute + 'feedback',
            feedbackModuleRouterHandler,
            feedbackModuleCleanupRouterHandler
        );
        registerRoute(defaultRoute + 'settings', optionsModuleRouterHandler);
        registerRoute(defaultRoute + 'settings/:subRoute', optionsModuleRouterHandler);
        /**
         * Adding this route to match the URL pattern on client-web
         * in order to support URLs like /settings/advertising/showpartnerlist/
         */
        registerRoute(
            defaultRoute + 'settings/:subRoute/:subRouteOption',
            optionsModuleRouterHandler
        );
        registerRoute(defaultRoute + 'compose', composeRouterHandler, composeCleanupRouteHandler);
        registerRoute(
            defaultRoute + 'compose/:draftId',
            composeRouterHandler,
            composeCleanupRouteHandler
        );

        // Support for the deep-linking URL retrieved from GraphAPIs
        // Few partner teams, like Todoist, query MS Graph to get the URL pattern to read an email.
        // Docs: https://docs.microsoft.com/en-us/previous-versions/office/office-365-api/api/version-2.0/mail-rest-operations#get-a-message
        // Following two URL patterns support this feature.
        // These need to be placed before "/:folderId" to avoid false positive route match
        registerRoute(defaultRoute + 'jsmvvmdeeplink/', mailRouteHandler, mailRouteCleanup);
        registerRoute(defaultRoute + 'deeplink', mailRouteHandler, mailRouteCleanup);

        registerRoute(
            defaultRoute +
                ':folderId/id/:conversationId/itemId/:itemId/immutableItemId/:immutableItemId',
            mailViewHandler,
            mailReadingPaneCleanUpRouteHandler
        );
        registerRoute(
            defaultRoute + ':folderId/id/:conversationId',
            mailReadingPaneRouteHandler,
            mailReadingPaneCleanUpRouteHandler
        );

        registerRoute(
            defaultRoute + ':folderId/immutableItemId/:immutableItemId',
            mailItemViewReadingPaneRouteEntryHandler,
            mailItemViewReadingPaneRouteExitHandler
        );

        registerRoute(
            defaultRoute + ':folderId/itemId/:itemId',
            mailItemViewReadingPaneRouteEntryHandler,
            mailItemViewReadingPaneRouteExitHandler
        );
        registerRoute(
            defaultRoute + ':folderId',
            mailFolderRouteHandler,
            mailFolderCleanupRouteHandler
        );

        // register deeplink routes within mail module.
        // These deeplinks are spawned out of mail module still. But end goal is to have a standalone
        // deeplink bootstrap that does not require mail module boot
        // Can be moved to separate register function if this grows
        registerRoute(defaultRoute + 'read/:itemId', mailRouteHandler, mailRouteCleanup);

        registerRsvpViewRoutes(defaultRoute);
        registerPeopleCardRoutes(defaultRoute);

        registerRoute(defaultRoute, defaultModuleRouterHandler);
    };
